import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Grid, IconButton, Popover, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { STATUS } from "../../utils/status";
import { getAnalytics } from "../../redux/slices/analytics";
import { getReview, setSelectedStageId } from "../../redux/slices/reviewerSlice";
import { METRIC_CONFIGS } from "./MetricConfigs";
import MetricCard from "./MetricCard";
import Error from "../error/error";
import { AnalyticsData } from "./types/analytics.type";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { AnalyticsGranularity, getDateRangeForGranularity } from "../../utils/helper";


const AnalyticsOverview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();

  const analytics = useSelector((state: RootState) => state.analytics.analytics.data as unknown as AnalyticsData | null);
  const getAnalyticsStatus: any = useSelector((state: RootState) => state.analytics.getStatus);
  const association_type = useSelector((state: RootState) => state.projects.association_type);
  const getReviewStages = useSelector((state: RootState) => state.reviewStage.reviewer);
  const selectedStageId = useSelector((state: RootState) => state.reviewStage.selectedStageId);
  const getReviewStatus = useSelector((state: RootState) => state.reviewStage.getReviewStatus);
  const dayDateRange = useMemo(() => getDateRangeForGranularity('day'), []);

  const [selectedChip, setSelectedChip] = useState<number | ''>('');
  const [startDate, setStartDate] = useState(dayDateRange.startDate);
  const [endDate, setEndDate] = useState(dayDateRange.endDate);
  const [appliedStartDate, setAppliedStartDate] = useState(dayDateRange.startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(dayDateRange.endDate);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [granularity, setGranularity] = useState<AnalyticsGranularity>('day');

  useEffect(() => {
    dispatch(getReview({ project_id: id || '' }))
  }, [dispatch, id]);

    useEffect(() => {
      if ( getReviewStages && getReviewStages.results && getReviewStages.results.length > 0) {
          const defaultStageId = getReviewStages.results[0].id;
          setSelectedChip(defaultStageId);
          dispatch(setSelectedStageId(defaultStageId));
      }
    }, [getReviewStages, dispatch]);

  useEffect(() => {
    if (!id || !selectedStageId || !appliedStartDate || !appliedEndDate) return;

    dispatch(
      getAnalytics({
        project_id: id,
        association_type,
        stage_id: selectedStageId,
        granularity,
        start_date: appliedStartDate || undefined,
        end_date: appliedEndDate || undefined,
      })
    );
  }, [dispatch, id, association_type, selectedStageId, appliedStartDate, appliedEndDate, granularity]);

  useEffect(() => {
    if (selectedChip) {
      setSelectedStageId(selectedChip);
    }
  }, [dispatch, selectedChip]);

  const handleChipClick = (clickedChipId: number) => {
    dispatch(setSelectedStageId(clickedChipId));
    setSelectedChip(clickedChipId)
  }

  const handleApplyDateFilter = (): void => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setFilterAnchorEl(null);
  };

  const handleClearDateFilter = (): void => {
    const dateRange = getDateRangeForGranularity(granularity);
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
    setAppliedStartDate(dateRange.startDate);
    setAppliedEndDate(dateRange.endDate);
    setFilterAnchorEl(null);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>): void => {
    setFilterAnchorEl(event.currentTarget);
  };

  const isFilterOpen = Boolean(filterAnchorEl);


  const selectedStageAnalytics = useMemo(() => {
    if (!analytics) return null;
    return analytics;
  }, [analytics]);

  const cards = useMemo(() => {
    if (!selectedStageAnalytics) return [];

    return METRIC_CONFIGS.map((config) => ({
      config,
      value: Number(selectedStageAnalytics[config.key] ?? 0),
    }));
  }, [selectedStageAnalytics]);


  return (
    <Box sx={{height: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ marginLeft: '10px', fontFamily: 'Montserrat', fontWeight: 700, fontSize: '20px', lineHeight: '14px' }}>
          Overview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={granularity}
            onChange={(_event, value: AnalyticsGranularity | null) => {
              if (!value) return;
              const dateRange = getDateRangeForGranularity(value);
              setGranularity(value);
              setStartDate(dateRange.startDate);
              setEndDate(dateRange.endDate);
              setAppliedStartDate(dateRange.startDate);
              setAppliedEndDate(dateRange.endDate);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '12px',
                padding: '4px 10px',
              },
            }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Filter by date range" placement="bottom-end" arrow>
            <IconButton
              aria-label="filter options"
              onClick={handleFilterClick}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                color: 'white',
                backgroundColor: '#3D85C6',
                '&:hover': { backgroundColor: '#2F6EA8' }
              }}
            >
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Popover
        open={isFilterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Date Filters</Typography>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="small" onClick={handleClearDateFilter} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
            <Button variant="contained" size="small" onClick={handleApplyDateFilter} sx={{ textTransform: 'none' }}>
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>

      <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {getReviewStatus === STATUS.SUCESS &&
          getReviewStages && getReviewStages.results && getReviewStages.results.length > 0 &&
          getReviewStages?.results?.map((option: any) => (
            <Chip
              key={option.id}
              label={option.name}
              sx={{
                marginLeft: '5px',
                marginBottom: '10px',
                marginTop: '5px',
                backgroundColor: selectedChip === option.id ? '#1976d2' : 'default',
                color: selectedChip === option.id ? 'white' : 'default',
                '&:hover': {
                  backgroundColor: selectedChip === option.id ? '#1565c0' : '#e0e0e0',
                },
              }}
              onClick={() => {
                handleChipClick(option.id)
              }}
            />
          ))}
      </Box>

      {getAnalyticsStatus === STATUS.PENDING && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {getAnalyticsStatus === STATUS.SUCESS && analytics && (
        Object.values(selectedStageAnalytics || {}).length > 0 ? <Grid container spacing={2} sx={{ fontFamily: 'Poppins, sans-serif' }}>
          {cards.map(({ config, value }) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={config.key}>
              <MetricCard
                config={config}
                value={value}
                isLoading={getAnalyticsStatus === STATUS.PENDING}
              />
            </Grid>
          ))}
        </Grid> : <Error message="No analytics data available for the selected stage." />
      )}

      {getAnalyticsStatus === STATUS.FAILED && (
        <Error message="Unable to load overview analytics. Please adjust filters and try again." />
      )}
    </Box>
  );
};

export default AnalyticsOverview;
